/**
 * Service Stripe pour l'application Dis Maman!
 * Gère les paiements et interactions avec l'API Stripe côté client
 */

class StripeService {
  constructor() {
    this.stripe = null;
    this.elements = null;
    this.isInitialized = false;
  }

  static getInstance() {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService();
    }
    return StripeService.instance;
  }

  /**
   * Initialise Stripe avec la clé publique
   */
  async initialize() {
    if (this.isInitialized && this.stripe) return;

    try {
      // Pour une PWA, on utilise l'API Stripe.js directement
      // Clé publique Stripe
      const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      
      if (!stripePublishableKey) {
        throw new Error('Clé publique Stripe manquante');
      }

      // Charger Stripe.js dynamiquement
      if (!window.Stripe) {
        await this.loadStripeJS();
      }

      this.stripe = window.Stripe(stripePublishableKey);
      
      if (!this.stripe) {
        throw new Error('Échec du chargement de Stripe');
      }

      this.isInitialized = true;
      console.log('✅ Stripe initialisé avec succès');
    } catch (error) {
      console.error('❌ Erreur initialisation Stripe:', error);
      throw error;
    }
  }

  /**
   * Charge dynamiquement la bibliothèque Stripe.js
   */
  async loadStripeJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Confirme un paiement avec Stripe
   */
  async confirmPayment(clientSecret, paymentMethodId) {
    if (!this.stripe) {
      await this.initialize();
    }

    if (!this.stripe) {
      return { success: false, error: 'Stripe non initialisé' };
    }

    try {
      const confirmParams = {};
      
      if (paymentMethodId) {
        confirmParams.payment_method = paymentMethodId;
      } else {
        // Si pas de méthode de paiement fournie, Stripe utilisera celle par défaut
        // ou demandera à l'utilisateur de la saisir
        confirmParams.return_url = window.location.origin + '/subscription-success';
      }

      const result = await this.stripe.confirmCardPayment(clientSecret, confirmParams);

      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Erreur de paiement' 
        };
      }

      return { 
        success: true, 
        paymentIntent: result.paymentIntent 
      };

    } catch (error) {
      console.error('Erreur confirmation paiement:', error);
      return { 
        success: false, 
        error: 'Erreur technique lors du paiement' 
      };
    }
  }

  /**
   * Crée un setup intent pour configurer un moyen de paiement
   */
  async createSetupIntent() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monetization/create-setup-intent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.detail || 'Erreur création setup intent' };
      }

      return { success: true, clientSecret: data.clientSecret };

    } catch (error) {
      console.error('Erreur setup intent:', error);
      return { success: false, error: 'Erreur de connexion' };
    }
  }

  /**
   * Confirme un setup intent
   */
  async confirmSetup(clientSecret) {
    if (!this.stripe) {
      await this.initialize();
    }

    if (!this.stripe) {
      return { success: false, error: 'Stripe non initialisé' };
    }

    try {
      const result = await this.stripe.confirmCardSetup(clientSecret, {
        return_url: window.location.origin + '/payment-setup-success'
      });

      if (result.error) {
        return { 
          success: false, 
          error: result.error.message || 'Erreur configuration paiement' 
        };
      }

      return { 
        success: true, 
        setupIntent: result.setupIntent 
      };

    } catch (error) {
      console.error('Erreur confirmation setup:', error);
      return { 
        success: false, 
        error: 'Erreur technique' 
      };
    }
  }

  /**
   * Crée des éléments de paiement Stripe
   */
  createElements(options = {}) {
    if (!this.stripe) {
      throw new Error('Stripe doit être initialisé avant de créer des éléments');
    }

    const defaultOptions = {
      fonts: [{
        cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
      }],
      ...options
    };

    this.elements = this.stripe.elements(defaultOptions);
    return this.elements;
  }

  /**
   * Crée un élément de carte de paiement
   */
  createCardElement(container, options = {}) {
    if (!this.elements) {
      this.createElements();
    }

    const defaultOptions = {
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          '::placeholder': {
            color: '#aab7c4',
          },
          fontFamily: 'Inter, system-ui, sans-serif',
        },
        invalid: {
          color: '#9e2146',
        },
      },
      hidePostalCode: true,
      ...options
    };

    const cardElement = this.elements.create('card', defaultOptions);
    
    if (container) {
      cardElement.mount(container);
    }

    return cardElement;
  }

  /**
   * Redirections vers le portail client Stripe (pour gérer les abonnements)
   */
  async redirectToCustomerPortal() {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Token manquant');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monetization/create-portal-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Erreur création session portail');
      }

      // Redirection vers le portail client Stripe
      window.location.href = data.url;

    } catch (error) {
      console.error('Erreur redirection portail:', error);
      throw error;
    }
  }

  /**
   * Obtient l'instance Stripe
   */
  getStripe() {
    return this.stripe;
  }

  /**
   * Obtient les éléments Stripe
   */
  getElements() {
    return this.elements;
  }

  /**
   * Formatage des erreurs Stripe en français
   */
  formatStripeError(error) {
    const errorMessages = {
      'card_declined': 'Votre carte a été refusée. Veuillez essayer avec une autre carte.',
      'expired_card': 'Votre carte a expiré. Veuillez utiliser une carte valide.',
      'incorrect_cvc': 'Le code de sécurité de votre carte est incorrect.',
      'processing_error': 'Une erreur est survenue lors du traitement. Veuillez réessayer.',
      'incorrect_number': 'Le numéro de carte est incorrect.',
      'invalid_expiry_month': 'Le mois d\'expiration de la carte est invalide.',
      'invalid_expiry_year': 'L\'année d\'expiration de la carte est invalide.',
      'invalid_cvc': 'Le code de sécurité de la carte est invalide.',
      'insufficient_funds': 'Votre carte ne dispose pas de fonds suffisants.',
      'generic_decline': 'Votre carte a été refusée. Contactez votre banque pour plus d\'informations.',
      'invalid_request_error': 'Demande invalide. Veuillez réessayer.',
      'api_connection_error': 'Problème de connexion. Vérifiez votre internet et réessayez.',
      'api_error': 'Erreur temporaire. Veuillez réessayer dans quelques minutes.',
      'authentication_error': 'Erreur d\'authentification. Veuillez vous reconnecter.',
      'rate_limit_error': 'Trop de tentatives. Veuillez attendre avant de réessayer.'
    };

    const errorType = error.type || error.code;
    return errorMessages[errorType] || error.message || 'Une erreur inattendue est survenue.';
  }
}

export default StripeService;